<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="1400px"
    persistent
    scrollable
  >
    <v-card class="border-radius-xl">
      <v-card-title class="d-flex justify-space-between align-center pa-6">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-3 material-icons-round">visibility</v-icon>
          <span class="text-h5 font-weight-bold text-typo">Campaign Entries - {{ campaign && campaign.title }}</span>
        </div>
        <v-btn icon @click="close" class="ml-4">
          <v-icon class="material-icons-round">close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0">
        <!-- Stats Summary -->
        <div class="pa-6" style="background-color: #f8f9fa;">
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-card class="card-shadow border-radius-xl text-center pa-4">
                <v-icon size="40" color="primary" class="mb-2 material-icons-round">format_list_numbered</v-icon>
                <div class="text-h4 font-weight-bold">{{ stats.totalEntries }}</div>
                <div class="text-subtitle-2 grey--text">Total Entries</div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card class="card-shadow border-radius-xl text-center pa-4">
                <v-icon size="40" color="success" class="mb-2 material-icons-round">group</v-icon>
                <div class="text-h4 font-weight-bold">{{ stats.uniqueParticipants }}</div>
                <div class="text-subtitle-2 grey--text">Unique Participants</div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card class="card-shadow border-radius-xl text-center pa-4">
                <v-icon size="40" color="orange" class="mb-2 material-icons-round">attach_money</v-icon>
                <div class="text-h4 font-weight-bold">${{ formatNumber(stats.totalRevenue) }}</div>
                <div class="text-subtitle-2 grey--text">Total Revenue</div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card class="card-shadow border-radius-xl text-center pa-4">
                <v-icon size="40" color="purple" class="mb-2 material-icons-round">card_giftcard</v-icon>
                <div class="text-h4 font-weight-bold">{{ stats.bonusEntries }}</div>
                <div class="text-subtitle-2 grey--text">Bonus Entries</div>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Filters and Actions -->
        <div class="pa-6 border-bottom">
          <v-row align="center">
            <v-col cols="12" md="3">
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search entries..."
                single-line
                hide-details
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="2">
              <v-select
                v-model="filterTier"
                :items="tierOptions"
                label="Filter by Tier"
                outlined
                dense
                clearable
                hide-details
              ></v-select>
            </v-col>
            <v-col cols="12" md="2">
              <v-select
                v-model="filterStatus"
                :items="statusOptions"
                label="Filter by Status"
                outlined
                dense
                clearable
                hide-details
              ></v-select>
            </v-col>
            <v-col cols="12" md="3">
              <!-- Spacer -->
            </v-col>
            <v-col cols="12" md="2">
              <v-btn
                color="green"
                dark
                block
                @click="exportEntries"
                :loading="exporting"
                class="elevation-2"
              >
                <v-icon left class="material-icons-round">download</v-icon>
                Export
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <!-- Entries Table -->
        <v-data-table
          :headers="headers"
          :items="filteredEntries"
          :search="search"
          :loading="loading"
          class="elevation-1"
          :items-per-page="15"
          :footer-props="{
            'items-per-page-options': [15, 25, 50, 100],
            'items-per-page-text': 'Rows per page:'
          }"
        >
          <!-- Participant Column -->
          <template v-slot:item.participant="{ item }">
            <div class="participant-info">
              <div class="font-weight-medium">{{ item.first_name }} {{ item.last_name }}</div>
              <div class="text-caption grey--text">{{ item.email }}</div>
              <div class="text-caption grey--text">{{ item.phone }}</div>
            </div>
          </template>

          <!-- Tier Column -->
          <template v-slot:item.tier="{ item }">
            <v-chip
              :color="getTierColor(item.tier_name)"
              text-color="white"
              small
            >
              {{ item.tier_name }}
            </v-chip>
          </template>

          <!-- Entries Column -->
          <template v-slot:item.entries="{ item }">
            <div class="entries-info">
              <div class="font-weight-medium">{{ item.total_entries }}</div>
              <div v-if="item.bonus_entries > 0" class="text-caption orange--text">
                +{{ item.bonus_entries }} bonus
              </div>
            </div>
          </template>

          <!-- Amount Column -->
          <template v-slot:item.amount="{ item }">
            <span class="font-weight-bold">${{ formatNumber(item.amount) }}</span>
          </template>

          <!-- Date Column -->
          <template v-slot:item.created_at="{ item }">
            <div class="date-info">
              <div>{{ formatDate(item.created_at) }}</div>
              <div class="text-caption grey--text">{{ formatTime(item.created_at) }}</div>
            </div>
          </template>

          <!-- Status Column -->
          <template v-slot:item.status="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              text-color="white"
              small
            >
              {{ item.status }}
            </v-chip>
          </template>

          <!-- Actions Column -->
          <template v-slot:item.actions="{ item }">
            <div class="d-flex gap-1">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    @click="viewEntryDetails(item)"
                  >
                    <v-icon small class="material-icons-round">visibility</v-icon>
                  </v-btn>
                </template>
                <span>View Details</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="info"
                    v-bind="attrs"
                    v-on="on"
                    @click="contactParticipant(item)"
                  >
                    <v-icon small class="material-icons-round">email</v-icon>
                  </v-btn>
                </template>
                <span>Contact</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.status === 'completed'">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="error"
                    v-bind="attrs"
                    v-on="on"
                    @click="refundEntry(item)"
                  >
                    <v-icon small class="material-icons-round">undo</v-icon>
                  </v-btn>
                </template>
                <span>Refund</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>

      <v-card-actions class="pa-6">
        <v-spacer></v-spacer>
        <v-btn
          color="grey"
          text
          @click="close"
          class="mr-2"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Entry Details Dialog -->
    <v-dialog v-model="showDetailsDialog" max-width="700px">
      <v-card v-if="selectedEntry" class="border-radius-xl">
        <v-card-title class="d-flex justify-space-between align-center pa-6">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-3 material-icons-round">info</v-icon>
            <span class="text-h6 font-weight-bold text-typo">Entry Details</span>
          </div>
          <v-btn icon @click="showDetailsDialog = false">
            <v-icon class="material-icons-round">close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="12" md="6">
              <h4 class="mb-2">Participant Information</h4>
              <p><strong>Name:</strong> {{ selectedEntry.first_name }} {{ selectedEntry.last_name }}</p>
              <p><strong>Email:</strong> {{ selectedEntry.email }}</p>
              <p><strong>Phone:</strong> {{ selectedEntry.phone }}</p>
            </v-col>
            <v-col cols="12" md="6">
              <h4 class="mb-2">Entry Information</h4>
              <p><strong>Tier:</strong> {{ selectedEntry.tier_name }}</p>
              <p><strong>Amount Paid:</strong> ${{ formatNumber(selectedEntry.amount) }}</p>
              <p><strong>Base Entries:</strong> {{ selectedEntry.base_entries }}</p>
              <p><strong>Bonus Entries:</strong> {{ selectedEntry.bonus_entries }}</p>
              <p><strong>Total Entries:</strong> {{ selectedEntry.total_entries }}</p>
              <p><strong>Entry Date:</strong> {{ formatDateTime(selectedEntry.created_at) }}</p>
              <p><strong>Status:</strong> 
                <v-chip :color="getStatusColor(selectedEntry.status)" text-color="white" small>
                  {{ selectedEntry.status }}
                </v-chip>
              </p>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
export default {
  name: 'SweepstakesEntriesDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    campaign: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      loading: false,
      exporting: false,
      search: '',
      filterTier: null,
      filterStatus: null,
      entries: [],
      stats: {
        totalEntries: 0,
        uniqueParticipants: 0,
        totalRevenue: 0,
        bonusEntries: 0
      },
      showDetailsDialog: false,
      selectedEntry: null,
      headers: [
        { text: 'Participant', value: 'participant', sortable: false, width: '200px' },
        { text: 'Tier', value: 'tier', sortable: true },
        { text: 'Entries', value: 'entries', sortable: true },
        { text: 'Amount', value: 'amount', sortable: true },
        { text: 'Date', value: 'created_at', sortable: true },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, width: '80px' }
      ],
      tierOptions: [],
      statusOptions: [
        { text: 'Completed', value: 'completed' },
        { text: 'Pending', value: 'pending' },
        { text: 'Failed', value: 'failed' },
        { text: 'Refunded', value: 'refunded' }
      ]
    }
  },
  computed: {
    filteredEntries() {
      let filtered = this.entries

      if (this.filterTier) {
        filtered = filtered.filter(entry => entry.tier_name === this.filterTier)
      }

      if (this.filterStatus) {
        filtered = filtered.filter(entry => entry.status === this.filterStatus)
      }

      return filtered
    }
  },
  watch: {
    value(newVal) {
      if (newVal && this.campaign) {
        this.loadEntries()
      }
    }
  },
  methods: {
    async loadEntries() {
      try {
        this.loading = true

        // Call the entries API
        const response = await window.axios.get(`/api/sweepstakes-admin/campaigns/${this.campaign.id}/entries`, {
          headers: {
            Authorization: "Bearer " + localStorage.getItem('token')
          }
        })

        if (response.data.flag === 1) {
          this.entries = response.data.entries
          this.stats = response.data.stats

          // Create tier options from entries
          this.tierOptions = [...new Set(this.entries.map(e => e.tier_name))].map(tier => ({
            text: tier,
            value: tier
          }))
        } else {
          this.entries = []
          this.stats = {
            totalEntries: 0,
            uniqueParticipants: 0,
            totalRevenue: 0,
            bonusEntries: 0
          }
          this.tierOptions = []
        }

      } catch (error) {
        console.error('Error loading entries:', error)
        this.$toast.error('Failed to load entries')
        this.entries = []
        this.stats = {
          totalEntries: 0,
          uniqueParticipants: 0,
          totalRevenue: 0,
          bonusEntries: 0
        }
        this.tierOptions = []
      } finally {
        this.loading = false
      }
    },

    async exportEntries() {
      try {
        this.exporting = true
        // Create CSV content
        const headers = ['First Name', 'Last Name', 'Email', 'Phone', 'Tier', 'Amount Paid', 'Base Entries', 'Bonus Entries', 'Total Entries', 'Entry Date', 'Status']
        const csvContent = [
          headers.join(','),
          ...this.filteredEntries.map(entry => [
            entry.first_name,
            entry.last_name,
            entry.email,
            entry.phone,
            entry.tier_name,
            entry.amount,
            entry.base_entries,
            entry.bonus_entries,
            entry.total_entries,
            this.formatDate(entry.created_at),
            entry.status
          ].join(','))
        ].join('\n')

        const blob = new Blob([csvContent], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `${this.campaign.title}_entries.csv`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        this.$toast.success('Entries exported successfully')
      } catch (error) {
        console.error('Error exporting entries:', error)
        this.$toast.error('Failed to export entries')
      } finally {
        this.exporting = false
      }
    },

    viewEntryDetails(entry) {
      this.selectedEntry = entry
      this.showDetailsDialog = true
    },

    contactParticipant(entry) {
      const subject = `Regarding your ${this.campaign.title} entry`
      const body = `Hello ${entry.first_name},\n\nThank you for participating in our ${this.campaign.title} sweepstakes.\n\nBest regards,\nAutoSyncX Team`
      window.open(`mailto:${entry.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`)
    },

    async refundEntry(entry) {
      if (await this.$confirm(`Are you sure you want to refund $${this.formatNumber(entry.amount)} to ${entry.first_name} ${entry.last_name}?`)) {
        try {
          // Refund API not available yet
          this.$toast.error('Refund API not implemented yet')
          // TODO: Implement actual refund API call when admin endpoints are available
        } catch (error) {
          console.error('Error refunding entry:', error)
          this.$toast.error('Failed to refund entry')
        }
      }
    },

    getTierColor(tierName) {
      const colors = ['primary', 'success', 'warning', 'info', 'purple']
      const hash = tierName.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0)
        return a & a
      }, 0)
      return colors[Math.abs(hash) % colors.length]
    },

    getStatusColor(status) {
      switch (status) {
        case 'completed': return 'success'
        case 'pending': return 'warning'
        case 'failed': return 'error'
        case 'refunded': return 'grey'
        default: return 'grey'
      }
    },

    formatNumber(value) {
      if (!value) return '0'
      return new Intl.NumberFormat('en-US').format(value)
    },

    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleDateString()
    },

    formatTime(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleTimeString()
    },

    formatDateTime(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleString()
    },

    close() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
/* Use existing admin styles */
.card-shadow {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.border-radius-xl {
  border-radius: 1rem;
}

.text-typo {
  color: #344767;
}

.material-icons-round {
  font-family: 'Material Icons Round';
}

.participant-info,
.entries-info,
.date-info {
  line-height: 1.2;
}

.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}

.gap-1 > * + * {
  margin-left: 4px;
}
</style>
